import { CommonEventsEnum } from '@hubxtech/hubx-client-analytics-package/lib/src/enum/common/commonEventsEnum';
import { EventsEnum } from '@hubxtech/hubx-client-analytics-package/lib/src/enum/pdfGenius/eventsEnum';

import { hubxAnalytic } from '@/lib/analytics/services/analytics';
import { getUtmParams } from '@/lib/analytics/utils/get-utm-params';
import { selectAuth } from '@/store/slices/authSlice';

export type HubxEventParams = Parameters<typeof hubxAnalytic.sendEvent>[0];

export type LogHubxEventParams = HubxEventParams;

type SourcePlacement = 'root' | 'commonString' | 'toolType' | 'headerTool';

const SOURCE_PLACEMENT_BY_EVENT = new Map<string, SourcePlacement>([
  [EventsEnum.BTN_HEADER, 'headerTool'],
  [EventsEnum.BTN_ALL_TOOLS, 'toolType'],
  [EventsEnum.BTN_COMPRESS, 'toolType'],
  [EventsEnum.BTN_CANCEL_UPLOAD, 'toolType'],
  [EventsEnum.BTN_CONTINUE_POPUP, 'toolType'],
  [EventsEnum.BTN_DOWNLOAD, 'toolType'],
  [EventsEnum.BTN_FINISH, 'toolType'],
  [EventsEnum.BTN_DOWNLOAD_EMAIL, 'toolType'],
  [EventsEnum.BTN_DOWNLOAD_POPUP, 'toolType'],
  [EventsEnum.BTN_FOOTER_ALL_TOOLS, 'toolType'],
  [EventsEnum.BTN_UPLOAD, 'toolType'],
  [EventsEnum.DOWNLOAD_SUCCESS, 'toolType'],
  [EventsEnum.AUTO_DOWNLOAD_SUCCESS, 'toolType'],
  [EventsEnum.FILE_UPLOAD_FAIL, 'toolType'],
  [EventsEnum.FILE_UPLOAD_SUCCESS, 'toolType'],
  [EventsEnum.LND_DOCUMENT_DOWNLOAD, 'toolType'],
  [EventsEnum.LND_EMAIL, 'toolType'],
  [EventsEnum.LND_FILE_VIEWER, 'toolType'],
  [EventsEnum.LND_CHECKOUT, 'toolType'],
  [EventsEnum.LND_DOWNLOAD_POPUP, 'toolType'],
  [EventsEnum.LND_PROCEED_POPUP, 'toolType'],
  [EventsEnum.BTN_PROCEED_POPUP, 'toolType'],
  [EventsEnum.CHECKOUT_PURCHASE_BUTTON_CLICK, 'toolType'],
  [EventsEnum.LND_LANDING, 'toolType'],
  [EventsEnum.LND_UPLOADING_ANIM, 'toolType'],
  [EventsEnum.LND_DOWNLOAD_VIEW, 'toolType'],
  [CommonEventsEnum.BTN_LOGIN_CONTINUE_EMAIL, 'commonString'],
  [CommonEventsEnum.INITIALIZATION_COMPLETE, 'commonString'],
  [CommonEventsEnum.LND_GET_STARTED, 'commonString'],
  [CommonEventsEnum.LND_PAYWALL, 'commonString'],
  [CommonEventsEnum.LND_REGISTER, 'commonString'],
  [CommonEventsEnum.LOGIN_SUCCESSFUL, 'commonString'],
  [CommonEventsEnum.PAYMENT_FAILED, 'commonString'],
  [CommonEventsEnum.REGISTER_SUCCESSFUL, 'commonString'],
]);

function getRecord(value: unknown): Record<string, unknown> | undefined {
  if (!value || typeof value !== 'object') {
    return undefined;
  }

  return value as Record<string, unknown>;
}

function getEventParamsRecord(
  params: LogHubxEventParams
): Record<string, unknown> | undefined {
  return getRecord((params as { event_params?: unknown }).event_params);
}

function flattenAnalyticsUserDataRecord(
  value: Record<string, unknown> | undefined
): Record<string, unknown> | undefined {
  if (!value) {
    return undefined;
  }

  const analyticsUserData = getRecord(value.analyticsUserData);

  if (!analyticsUserData) {
    return value;
  }

  const { analyticsUserData: _analyticsUserData, ...rest } = value;

  return {
    ...rest,
    ...analyticsUserData,
  };
}

function buildUserProperties(
  params: LogHubxEventParams,
  auth: ReturnType<typeof selectAuth>,
  utmParams: ReturnType<typeof getUtmParams>
) {
  const userProperties = flattenAnalyticsUserDataRecord(
    getRecord(params.user_properties)
  );

  return {
    ...utmParams,
    user_id: params.user_id ?? auth?.user?.uid,
    email: auth?.user?.email,
    first_name: auth?.user?.name?.split(' ')[0],
    last_name: auth?.user?.name?.split(' ').slice(1).join(' '),
    ...(userProperties ?? {}),
  };
}

function buildUrlParams(params: LogHubxEventParams, currentUrl: string) {
  const origin = window.location.origin;

  return {
    current_url: origin + currentUrl,
    previous_url: window.previous_url
      ? origin + window.previous_url
      : undefined,
    ...(params.url_params ?? {}),
  };
}

function getSourcePlacement(eventName: string): SourcePlacement {
  return SOURCE_PLACEMENT_BY_EVENT.get(eventName) ?? 'root';
}

function readSourceValue(value: unknown): string | undefined {
  if (typeof value === 'string') {
    return value;
  }

  const sourceRecord = getRecord(value);

  if (typeof sourceRecord?.toolType === 'string') {
    return sourceRecord.toolType;
  }

  if (typeof sourceRecord?.headerTool === 'string') {
    return sourceRecord.headerTool;
  }

  return undefined;
}

function hasStructuredSource(
  value: unknown,
  key: 'toolType' | 'headerTool'
): boolean {
  const sourceRecord = getRecord(value);

  return typeof sourceRecord?.[key] === 'string';
}

export function resolveSourceParam(
  params: LogHubxEventParams
): string | undefined {
  const rootSource = typeof params.source === 'string' ? params.source : undefined;

  return readSourceValue(getEventParamsRecord(params)?.source) ?? rootSource;
}

function buildEventParams(params: LogHubxEventParams) {
  const rawEventParams = getEventParamsRecord(params);
  const eventParams = rawEventParams ? { ...rawEventParams } : undefined;
  const userData = flattenAnalyticsUserDataRecord(
    getRecord(eventParams?.user_data)
  );
  const sourcePlacement = getSourcePlacement(params.eventName);
  const sourceValue = resolveSourceParam(params);

  if (eventParams && userData) {
    eventParams.user_data = userData;
  }

  if (!sourceValue || sourcePlacement === 'root') {
    return eventParams;
  }

  if (sourcePlacement === 'headerTool') {
    if (hasStructuredSource(eventParams?.source, 'headerTool')) {
      return eventParams;
    }

    return {
      ...(eventParams ?? {}),
      source: {
        ...(getRecord(eventParams?.source) ?? {}),
        headerTool: sourceValue,
      },
    };
  }

  if (sourcePlacement === 'toolType') {
    if (hasStructuredSource(eventParams?.source, 'toolType')) {
      return eventParams;
    }

    return {
      ...(eventParams ?? {}),
      source: {
        ...(getRecord(eventParams?.source) ?? {}),
        toolType: sourceValue,
      },
    };
  }

  if (params.eventName === CommonEventsEnum.LND_PAYWALL) {
    return {
      ...(eventParams ?? {}),
      source: sourceValue,
    };
  }

  if (typeof eventParams?.source === 'string') {
    return eventParams;
  }

  if (getRecord(eventParams?.source)) {
    return eventParams;
  }

  return {
    ...(eventParams ?? {}),
    source: sourceValue,
  };
}

function shouldKeepRootSource(params: LogHubxEventParams): boolean {
  return getSourcePlacement(params.eventName) === 'root';
}

export function buildHubxPayload(
  params: LogHubxEventParams,
  auth: ReturnType<typeof selectAuth>,
  utmParams: ReturnType<typeof getUtmParams>,
  currentUrl: string,
  firstVisitTimestamp: string,
  eventTimestamp: string,
  clientId: string
): HubxEventParams {
  const payload = {
    ...params,
    event_params: buildEventParams(params),
    user_id: params.user_id ?? auth?.user?.uid,
    user_pseudo_id: clientId,
    event_timestamp: eventTimestamp,
    install_timestamp: firstVisitTimestamp,
    user_properties: buildUserProperties(params, auth, utmParams),
    url_params: buildUrlParams(params, currentUrl),
  };

  if (!shouldKeepRootSource(params)) {
    delete (payload as Partial<HubxEventParams>).source;
  }

  return payload as HubxEventParams;
}
